import { useState } from "react";
import { authStore } from "../auth";
import { Button } from "../components/Button";
import { VerifyStepList } from "../components/VerifyStepList";
import { useFlow } from "../flow/FlowContext";
import { buildApplicationFromAnswers } from "../domain/buildApplication";
import { formatBytes } from "../media/createMediaAsset";
import { reviewQueueStore } from "../review/queueStore";
import { useT } from "../i18n";

export function VerifyReviewScreen() {
  const { answers, next } = useFlow();
  const t = useT();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const id = answers.governmentId;
  const video = answers.confirmationVideo;
  const photos = answers.profilePhotos;
  const userId = authStore.getUserId();

  const submitAndContinue = async () => {
    setError(null);
    setBusy(true);
    try {
      let uid = authStore.getUserId();
      if (!uid) {
        const auth = await authStore.ensureRegistered(answers.email, answers.password);
        if (!auth.ok) {
          setError(auth.error);
          return;
        }
        uid = auth.user.id;
      }

      const app = buildApplicationFromAnswers(answers, uid);
      reviewQueueStore.submitApplication(app);
      next();
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div
        className="screen-enter"
        style={{ flex: 1, display: "flex", flexDirection: "column", gap: 18 }}
      >
        <header className="screen-head">
          <div className="screen-head__eyebrow">{t("verify.review.eyebrow")}</div>
          <h1 className="screen-head__title">{t("verify.review.title")}</h1>
          <p className="screen-head__text">
            {t("verify.review.text", {
              underUser: userId ? t("verify.review.underUser", { userId }) : "",
            })}
          </p>
        </header>
        <VerifyStepList
          statuses={{
            id: "done",
            video: "done",
            photos: "done",
            review: "active",
          }}
        />

        <ul className="upload-summary">
          <li>
            <div>
              <div className="upload-summary__label">{t("verify.review.governmentId")}</div>
              <div className="upload-summary__meta">
                {id ? `${id.fileName} · ${formatBytes(id.sizeBytes)}` : t("verify.review.missing")}
              </div>
            </div>
            <span className="status-chip">{id?.reviewStatus ?? "—"}</span>
          </li>
          <li>
            <div>
              <div className="upload-summary__label">{t("verify.review.confirmationVideo")}</div>
              <div className="upload-summary__meta">
                {video
                  ? `${video.fileName} · ${formatBytes(video.sizeBytes)}`
                  : t("verify.review.missing")}
              </div>
            </div>
            <span className="status-chip">{video?.reviewStatus ?? "—"}</span>
          </li>
          <li>
            <div>
              <div className="upload-summary__label">{t("verify.review.photos")}</div>
              <div className="upload-summary__meta">
                {t("verify.review.filesReady", { count: photos.length })}
              </div>
            </div>
            <span className="status-chip">
              {photos.length && photos.every((p) => p.reviewStatus === "pending_review")
                ? "pending_review"
                : "—"}
            </span>
          </li>
        </ul>

        {error ? <p className="capture__error">{error}</p> : null}

        <div className="review-card">
          <div className="review-card__seal" aria-hidden>
            {t("common.ellipsis")}
          </div>
          <h2 className="review-card__title">{t("verify.review.submittedTitle")}</h2>
          <p className="review-card__text">{t("verify.review.submittedText")}</p>
        </div>
      </div>
      <div className="shell__footer" style={{ paddingLeft: 0, paddingRight: 0 }}>
        <Button onClick={submitAndContinue} disabled={busy}>
          {busy ? t("common.submitting") : t("verify.review.submitContinue")}
        </Button>
      </div>
    </>
  );
}
